import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";

const TYPE_COLORS = {
    USAGE: "bg-gray-100 text-gray-700",
    CREDIT_PURCHASE: "bg-green-100 text-green-700",
    AUTO_CHARGE: "bg-blue-100 text-blue-700",
    SUBSCRIPTION_RENEWAL: "bg-purple-100 text-purple-700",
    ADMIN_ADDITION: "bg-amber-100 text-amber-700",
};

function formatAmount(cost, isUsd) {
    if (cost == null) return "-";
    if (isUsd) return "$" + (cost < 0.01 ? cost.toFixed(4) : cost.toFixed(2));
    return (cost < 0.01 ? cost.toFixed(4) : cost.toFixed(2)) + " cr";
}

export default function CreditUsageTable({ logs = [], page = 1, totalPages = 1, onPageChange, platform = "agentui" }) {
    const { t } = useTranslation();
    const isUsd = platform !== "agentui";

    if (!logs.length) {
        return (
            <div className="text-center text-sm text-muted-foreground py-8">
                {t("billing.usage.noData")}
            </div>
        );
    }

    return (
        <div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b text-left text-xs text-gray-500">
                            <th className="pb-2 pr-4">{t("billing.usage.date")}</th>
                            <th className="pb-2 pr-4">{t("billing.usage.description")}</th>
                            <th className="pb-2 pr-4">{t("billing.usage.type")}</th>
                            <th className="pb-2 pr-4 text-right">{isUsd ? t("billing.usage.amountUsed") : t("billing.usage.creditsUsed")}</th>
                            <th className="pb-2 text-right">{t("billing.usage.balanceAfter")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map((log) => (
                            <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">
                                    {format(new Date(log.usedAt), "MMM d, yyyy")}
                                </td>
                                <td className="py-2 pr-4 max-w-[250px]">
                                    <span className="block truncate">{log.description || "-"}</span>
                                    {log.count > 1 && (
                                        <span className="text-xs text-gray-400">{log.count} calls</span>
                                    )}
                                </td>
                                <td className="py-2 pr-4">
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${TYPE_COLORS[log.transactionType] || TYPE_COLORS.USAGE}`}>
                                        {log.transactionType?.replace(/_/g, " ") || "USAGE"}
                                    </span>
                                </td>
                                <td className={`py-2 pr-4 text-right font-medium ${log.creditsAdded ? "text-green-600" : ""}`}>
                                    {log.creditsAdded ? "+" : ""}{formatAmount(log.creditsUsed, isUsd)}
                                </td>
                                <td className="py-2 text-right text-gray-500">{formatAmount(log.balanceAfter, isUsd)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <span className="text-xs text-gray-500">
                        {t("billing.usage.pageOf", { page, totalPages })}
                    </span>
                    <div className="flex gap-1">
                        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
